import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { appointmentAPI, promotionsAPI, pagesAPI } from '../services/api';
import { 
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Loader,
  Send,
  ArrowRight,
  CalendarDays,
  MapPin,
  Video,
  PhoneCall,
  Users,
  Zap,
  Star,
  Shield,
  HeadphonesIcon
} from 'lucide-react';
import { gsap } from 'gsap';

const Appointment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    purpose: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const appointmentRef = useRef(null);

  // Banner promotions state
  const [promotions, setPromotions] = useState([]);
  const [topBannerPromotions, setTopBannerPromotions] = useState([]);
  const [topBannerIndex, setTopBannerIndex] = useState(0);

  // Page content state
  const [pageContent, setPageContent] = useState({
    main_title: 'Schedule a',
    gradient_title: 'Meeting',
    description: 'Book a consultation with our team to discuss your project requirements and get expert guidance.',
    form_title: 'Schedule Your Meeting',
    form_description: 'Fill out the form below and we\'ll confirm your appointment within 2 hours.',
    form_fields: {
      name: { label: 'Full Name', placeholder: 'Enter your full name' },
      email: { label: 'Email Address', placeholder: 'Enter your email address' },
      phone: { label: 'Phone Number', placeholder: 'Enter your phone number' },
      date: { label: 'Preferred Date', placeholder: 'Select date' },
      time: { label: 'Preferred Time', placeholder: 'Select time' },
      purpose: { label: 'Meeting Purpose', placeholder: 'Select purpose' },
      message: { label: 'Additional Message', placeholder: 'Tell us more about your project or any specific requirements...' }
    },
    time_options: [
      { value: '09:00', label: '9:00 AM' },
      { value: '10:00', label: '10:00 AM' },
      { value: '11:00', label: '11:00 AM' },
      { value: '12:00', label: '12:00 PM' },
      { value: '13:00', label: '1:00 PM' },
      { value: '14:00', label: '2:00 PM' },
      { value: '15:00', label: '3:00 PM' },
      { value: '16:00', label: '4:00 PM' },
      { value: '17:00', label: '5:00 PM' }
    ],
    purpose_options: [
      { value: 'project-discussion', label: 'Project Discussion' },
      { value: 'consultation', label: 'General Consultation' },
      { value: 'quote-request', label: 'Quote Request' },
      { value: 'technical-support', label: 'Technical Support' },
      { value: 'partnership', label: 'Partnership Discussion' },
      { value: 'other', label: 'Other' }
    ],
    submit_button: { text: 'Schedule Meeting', icon: 'Send' },
    benefits_title: 'Why Schedule a Meeting?',
    benefits: [
      { id: 1, icon: 'Users', title: 'Expert Consultation', description: 'Get personalized advice from our experienced team' },
      { id: 2, icon: 'Video', title: 'Video Call', description: 'Meet face-to-face via video conference' },
      { id: 3, icon: 'Calendar', title: 'Flexible Scheduling', description: 'Choose a time that works best for you' },
      { id: 4, icon: 'Shield', title: 'Free Consultation', description: 'No cost for initial project discussion' }
    ],
    contact_info: {
      title: 'Get in Touch',
      phone: '+91 9876543210',
      email: 'info@niswainfotech.com',
      address: '123 Business District, Andheri East, Mumbai, Maharashtra 400069'
    },
    cta_title: 'Need Immediate Assistance?',
    cta_description: 'Can\'t wait for a scheduled meeting? Contact us directly for urgent inquiries.',
    cta_buttons: [
      { title: 'Contact Us', icon: 'MessageCircle', link: '/contact' },
      { title: 'Call Now', icon: 'PhoneCall', link: 'tel:+919876543210' }
    ]
  });

  // Fetch page content
  const fetchPageContent = async () => {
    try {
      const response = await pagesAPI.getByType('appointment');
      if (response?.data?.success && response.data.data?.content) {
        setPageContent(response.data.data.content);
      }
    } catch (error) {
      console.error('Error fetching appointment page content:', error);
    }
  };

  // Fetch promotions
  const fetchPromotions = async () => {
    try {
      const response = await promotionsAPI.getAll({ 
        type: 'banner',
        position: 'top',
        is_active: true 
      });
      if (response?.data?.success) {
        setTopBannerPromotions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  };

  // GSAP animations
  useEffect(() => {
    if (appointmentRef.current) {
      gsap.fromTo(appointmentRef.current, 
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
      );
    }
  }, []);

  // Banner rotation
  useEffect(() => {
    if (topBannerPromotions.length > 1) {
      const interval = setInterval(() => {
        setTopBannerIndex((prev) => (prev + 1) % topBannerPromotions.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [topBannerPromotions]);

  useEffect(() => {
    fetchPageContent();
    fetchPromotions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error message when user starts typing
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      console.log('Submitting appointment data:', formData);
      const response = await appointmentAPI.submit(formData);
      if (response?.data?.success) {
        setSubmitStatus('success');
        setErrorMessage('');
        setFormData({
          name: '',
          email: '',
          phone: '',
          preferredDate: '',
          preferredTime: '',
          purpose: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Appointment submission error:', error);
      console.error('Error response:', error.response?.data);
      setSubmitStatus('error');
      
      // Extract error message from response
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage('Failed to submit appointment. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      'Calendar': Calendar,
      'Clock': Clock,
      'User': User,
      'Mail': Mail,
      'Phone': Phone,
      'MessageCircle': MessageCircle,
      'CheckCircle': CheckCircle,
      'AlertCircle': AlertCircle,
      'Loader': Loader,
      'Send': Send,
      'ArrowRight': ArrowRight,
      'CalendarDays': CalendarDays,
      'MapPin': MapPin,
      'Video': Video,
      'PhoneCall': PhoneCall,
      'Users': Users,
      'Zap': Zap,
      'Star': Star,
      'Shield': Shield,
      'HeadphonesIcon': HeadphonesIcon
    };
    return icons[iconName] || Calendar;
  };

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Top Banner Promotions */}
      {topBannerPromotions.length > 0 && (
        <div className="relative bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-4 overflow-hidden">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <Zap className="w-5 h-5" />
              <span className="text-sm font-medium">
                {topBannerPromotions[topBannerIndex]?.title || 'Special Offer'}
              </span>
            </div>
          </div>
        </div>
      )}

      <div ref={appointmentRef} className="container mx-auto px-4 pb-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4">
            {pageContent.main_title}{' '}
            <span className="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
              {pageContent.gradient_title}
            </span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {pageContent.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Form Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {pageContent.form_title}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {pageContent.form_description}
              </p>
            </div>

            {/* Error Notification */}
            {errorMessage && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" />
                  <p className="text-red-700 dark:text-red-300 font-medium">
                    {errorMessage}
                  </p>
                </div>
              </div>
            )}

            {/* Success Notification */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
                  <p className="text-green-700 dark:text-green-300 font-medium">
                    Appointment submitted successfully! We will confirm your appointment within 2 hours.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {pageContent.form_fields.name.label} *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    placeholder={pageContent.form_fields.name.placeholder}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {pageContent.form_fields.email.label} *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    placeholder={pageContent.form_fields.email.placeholder}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {pageContent.form_fields.phone.label} *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    placeholder={pageContent.form_fields.phone.placeholder}
                  />
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {pageContent.form_fields.date.label} *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      required
                      min={getCurrentDate()}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {pageContent.form_fields.time.label} *
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    >
                      <option value="">{pageContent.form_fields.time.placeholder}</option>
                      {pageContent.time_options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {pageContent.form_fields.purpose.label} *
                </label>
                <div className="relative">
                  <MessageCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                  >
                    <option value="">{pageContent.form_fields.purpose.placeholder}</option>
                    {pageContent.purpose_options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {pageContent.form_fields.message.label}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors resize-none"
                  placeholder={pageContent.form_fields.message.placeholder}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-primary-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Scheduling...</span>
                  </>
                ) : (
                  <>
                    <span>{pageContent.submit_button.text}</span>
                    {(() => {
                      const IconComponent = getIconComponent(pageContent.submit_button.icon);
                      return <IconComponent className="w-5 h-5" />;
                    })()}
                  </>
                )}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="flex items-center space-x-2 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span className="text-green-800 dark:text-green-200">
                    Meeting scheduled successfully! We'll confirm your appointment within 2 hours.
                  </span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center space-x-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  <span className="text-red-800 dark:text-red-200">
                    Something went wrong. Please try again or contact us directly.
                  </span>
                </div>
              )}
            </form>
          </div>

          {/* Benefits Section */}
          <div className="space-y-8">
            {/* Benefits */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {pageContent.benefits_title}
              </h3>
              <div className="space-y-6">
                {pageContent.benefits.map((benefit) => {
                  const IconComponent = getIconComponent(benefit.icon);
                  return (
                    <div key={benefit.id} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-primary-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {benefit.title}
                        </h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-br from-primary-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">{pageContent.contact_info.title}</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5" />
                  <span>{pageContent.contact_info.phone}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5" />
                  <span>{pageContent.contact_info.email}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 mt-1" />
                  <span>{pageContent.contact_info.address}</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {pageContent.cta_title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {pageContent.cta_description}
              </p>
              <div className="space-y-3">
                {pageContent.cta_buttons.map((button, index) => {
                  const IconComponent = getIconComponent(button.icon);
                  return (
                    <Link
                      key={index}
                      to={button.link}
                      className="flex items-center justify-center space-x-2 w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <IconComponent className="w-5 h-5" />
                      <span>{button.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Appointment; 